import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  TrophyIcon,
  CrownIcon,
  MedalIcon,
  GlobeHemisphereWestIcon,
  StackIcon,
  SpinnerGapIcon,
} from "@phosphor-icons/react";
import {
  getGlobalLeaderboard,
  getModuleLeaderboard,
  getModules,
} from "../api";
import { toast } from "sonner";

const accColor = (v) => (v >= 90 ? "#10b981" : v >= 75 ? "#f59e0b" : "#e11d48");

const RankBadge = ({ rank }) => {
  if (rank === 1)
    return <CrownIcon size={20} weight="fill" color="#f59e0b" data-testid="rank-1" />;
  if (rank === 2) return <MedalIcon size={19} weight="fill" color="#a3a3a3" />;
  if (rank === 3) return <MedalIcon size={19} weight="fill" color="#b45309" />;
  return <span className="font-mono text-sm text-[#a3a3a3] font-semibold">{rank}</span>;
};

export default function Leaderboard() {
  const [tab, setTab] = useState("global");
  const [modules, setModules] = useState([]);
  const [activeModule, setActiveModule] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getModules().then((m) => {
      setModules(m);
      setActiveModule(m[0]?.id || null);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    const load =
      tab === "global"
        ? getGlobalLeaderboard()
        : activeModule
        ? getModuleLeaderboard(activeModule)
        : Promise.resolve([]);
    load
      .then(setRows)
      .catch(() => toast.error("Failed to load leaderboard"))
      .finally(() => setLoading(false));
  }, [tab, activeModule]);

  return (
    <div className="grid-bg min-h-[calc(100vh-4rem)]">
      <div className="max-w-[1100px] mx-auto px-6 md:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="label mb-2 flex items-center gap-2">
            <TrophyIcon size={15} weight="bold" /> Trainee Rankings
          </div>
          <h1 className="font-head font-black text-4xl md:text-5xl tracking-tighter">
            QA Accuracy Leaderboard
          </h1>
        </motion.div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b-2 border-[#0a0a0a] mb-6">
          {[
            { key: "global", label: "Global", Icon: GlobeHemisphereWestIcon },
            { key: "module", label: "By Module", Icon: StackIcon },
          ].map((t) => {
            const { Icon } = t;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`leaderboard-tab-${t.key}`}
                className={`px-5 py-2.5 text-sm font-semibold flex items-center gap-2 -mb-0.5 border-b-2 transition-colors ${
                  active
                    ? "border-[#0a0a0a] text-[#0a0a0a]"
                    : "border-transparent text-[#a3a3a3] hover:text-[#0a0a0a]"
                }`}
              >
                <Icon size={16} weight="bold" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "module" && (
          <div className="flex flex-wrap gap-2 mb-6">
            {modules.map((m) => (
              <button
                key={m.id}
                onClick={() => setActiveModule(m.id)}
                data-testid={`module-filter-${m.id}`}
                className={`px-3.5 py-2 text-xs font-semibold border transition-colors ${
                  activeModule === m.id
                    ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                    : "bg-white border-[#e5e5e5] hover:border-[#0a0a0a]"
                }`}
              >
                {m.title}
              </button>
            ))}
          </div>
        )}

        {/* Table */}
        <div className="border border-[#e5e5e5] bg-white" data-testid="leaderboard-table">
          <div className="grid grid-cols-[56px_1fr_auto] md:grid-cols-[56px_1fr_140px_120px] gap-4 px-5 py-3 border-b border-[#e5e5e5] bg-[#f8f9fa]">
            <div className="label text-[0.58rem]">Rank</div>
            <div className="label text-[0.58rem]">Trainee</div>
            <div className="label text-[0.58rem] hidden md:block">
              {tab === "global" ? "Modules" : "Score"}
            </div>
            <div className="label text-[0.58rem] text-right">Accuracy</div>
          </div>

          {loading ? (
            <div className="py-16 flex justify-center">
              <SpinnerGapIcon size={26} weight="bold" className="animate-spin text-[#0a0a0a]" />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-[#525252] text-sm" data-testid="leaderboard-empty">
              No completed sessions yet. Finish a module to appear on the board.
            </div>
          ) : (
            rows.map((r, i) => {
              const acc = tab === "global" ? r.avg_accuracy : r.accuracy;
              return (
                <motion.div
                  key={`${r.trainee_name}-${i}`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  data-testid={`leaderboard-row-${i}`}
                  className={`grid grid-cols-[56px_1fr_auto] md:grid-cols-[56px_1fr_140px_120px] gap-4 px-5 py-4 border-b border-[#e5e5e5] last:border-0 items-center ${
                    i === 0 ? "bg-[#f59e0b]/[0.06]" : ""
                  }`}
                >
                  <div className="flex items-center justify-center">
                    <RankBadge rank={i + 1} />
                  </div>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-8 w-8 bg-[#0a0a0a] text-white flex items-center justify-center font-head font-bold text-xs shrink-0">
                      {r.trainee_name.slice(0, 2).toUpperCase()}
                    </div>
                    <span className="font-semibold text-sm truncate">{r.trainee_name}</span>
                  </div>
                  <div className="hidden md:block text-sm text-[#525252]">
                    {tab === "global"
                      ? `${r.modules_completed} completed`
                      : `${r.correct}/${r.total}`}
                  </div>
                  <div className="text-right">
                    <span
                      className="font-head font-black text-lg tracking-tighter"
                      style={{ color: accColor(acc) }}
                    >
                      {acc}%
                    </span>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
